import React from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { formatUSD } from '@sparefoot/react-core/utils/format';

import { unitPropType } from 'config/propTypes';

import { FeeWarningToolTip } from 'components/unit/FeeWarningToolTip';

import { Paragraph, Text } from 'components/core/Typography';
import { Link } from 'components/core/Link';

import { TrackingClient } from '@sparefoot/tracking-client';
// Bare SCSS import ensures StarRating styles load before StackedUnit styles to
// preserve MiniCssExtractPlugin ordering (avoids specificity conflicts).
import 'components/review/StarRating/StarRating.scss';
import { Tooltip } from 'components/core/Tooltip';
import { Badge } from 'components/molecules/Badge';
import { FlexRow } from '../../atoms/Flex/FlexRow';
import { FlexColumn } from 'components/atoms/Flex/FlexColumn';

import './NewStackedUnits.scss';

export function NoUnits({ unit }) {
	return (
		<div className="stacked-no-units">
			<Paragraph
				size="smallMedium"
				weight="standard"
				color="placeholderGray"
				last
			>
				No{' '}
				<Text
					size="small"
					weight="extraBold"
					color="placeholderGray"
				>
					{unit.size}
				</Text>{' '}
				Units Available
			</Paragraph>
		</div>
	);
}

NoUnits.propTypes = {
	unit: unitPropType.isRequired,
};

export function StackedUnit({ unit, facility, baseUrl }) {
	if (unit?.quantity === 0) {
		return <NoUnits unit={unit} />;
	}

	const trackUnitSelect = () => {
		if (!unit) return;

		const location = facility?.location
			? `${facility.location.city}, ${facility.location.fullState}`
			: 'Unknown Location';

		TrackingClient.trackFacilityUnitSelected({
			facility: {
				id: facility?.id || 'Unknown Facility ID',
				name: facility?.name || 'Unknown Facility',
				location: location,
			},
			unit: {
				currency: 'usd',
				id: unit.id,
				variant_size: unit?.sizeText || 'Unknown Size',
				price: unit?.price?.meta || 'Unknown Price',
				quantity: 1,
			},
			unit_id: unit.id,
			unit_promo: unit.promotion || 'No Promo',
		});
	};

	const regularPrice = get(unit, 'price.regular');
	const salePrice = get(unit, 'price.meta');
	const showStrikethrough = regularPrice && regularPrice > salePrice;

	return (
		<Link
			className="new-stacked-unit"
			href={`${baseUrl}${unit.url}`}
			rel="nofollow"
			onClick={trackUnitSelect}
			trackEcommerceClick
			ecommerceProps={{
				price: get(unit, 'price.meta', ''),
			}}
		>
			<FlexRow
				className="unit-container"
				justifyBetween
				alignCenter
			>
				<FlexRow
					alignCenter
					gapTiny
				>
					<Paragraph
						weight="extraBold"
						last
					>
						{unit.size}
					</Paragraph>
					{!!unit.special && (
						<Tooltip
							className="promo-tooltip"
							content={
								<FlexColumn
									alignCenter
									className="promo-tooltip-content"
								>
									<Text
										size="xsmall"
										weight="extraBold"
										last
									>
										PROMO:
									</Text>
									<Text
										last
										size="tiny"
										weight="standard"
									>
										{unit.special}
									</Text>
								</FlexColumn>
							}
						>
							<Badge
								className="unit-promo tooltip-icon"
								badgeText={
									<Text
										size="xsmall"
										weight="extraBold"
										color="greenHero"
									>
										Deal
									</Text>
								}
								badgeColor="green"
							/>
						</Tooltip>
					)}
					{unit.quantity && unit.quantity < 4 && (
						<Text
							className="scarcity"
							size="tiny"
							weight="medium"
							color="orange"
							last
						>
							{unit.quantity} left{' '}
						</Text>
					)}
				</FlexRow>
				{!unit.isOutOfBookingRange && (
					<div className="unit-price">
						<div
							style={{
								display: 'flex',
								alignItems: 'baseline',
								justifyContent: 'flex-end',
								gap: '8px',
							}}
							className="unit-price-div"
						>
							{showStrikethrough && (
								<Text
									strikethrough
									color="placeholderGray"
									last
									className="regular-price"
								>
									{formatUSD(regularPrice)}
								</Text>
							)}
							<Paragraph
								weight="extraBold"
								last
								className="sale-price"
							>
								{formatUSD(salePrice)}
							</Paragraph>
						</div>
						{facility.showFeeWarning && facility.totalFees > 0 && (
							<FeeWarningToolTip
								facility={facility}
								className="fee-tooltip"
							/>
						)}
					</div>
				)}
			</FlexRow>
		</Link>
	);
}

StackedUnit.propTypes = {
	unit: unitPropType.isRequired,
	facility: PropTypes.object.isRequired,
	baseUrl: PropTypes.string,
};

export default StackedUnit;
